import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

const getToken = () => localStorage.getItem('rz_token');

async function fetchReport(endpoint, params = '') {
  const r = await fetch(`/api/reports/${endpoint}${params}`, {
    headers: { 'Authorization': 'Bearer ' + getToken() }
  });
  return r.json();
}

export default function Reports() {
  const [tab, setTab] = useState('inventory');
  const [inventoryData, setInventoryData] = useState(null);
  const [dealData, setDealData] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('30');
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (tab === 'inventory' && !inventoryData) loadInventory();
    if (tab === 'deals' && !dealData) loadDeals();
  }, [tab]);

  const loadInventory = async () => {
    setLoading(true);
    const d = await fetchReport('inventory');
    setInventoryData(d);
    setLoading(false);
  };

  const loadDeals = async () => {
    setLoading(true);
    const d = await fetchReport('deals', `?period=${period}`);
    setDealData(d);
    setLoading(false);
  };

  const loadRecommendations = async () => {
    setAiLoading(true);
    const d = await fetchReport('recommendations');
    setRecommendations(d);
    setAiLoading(false);
  };

  const tabs = [
    { id: 'inventory', label: '📦 Inventory Intelligence' },
    { id: 'deals', label: '🤝 Deal Reports' },
    { id: 'ai', label: '🤖 AI Recommendations' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Reports & <span>Intelligence</span></h1>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: '1px solid #222', paddingBottom: 0 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '10px 20px', background: 'transparent', border: 'none',
            borderBottom: tab === t.id ? '2px solid #cc0000' : '2px solid transparent',
            color: tab === t.id ? '#cc0000' : '#888', cursor: 'pointer', fontSize: 14, fontWeight: 600
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'inventory' && (
        <div>
          {loading ? <div className="loading">Analyzing inventory...</div> : inventoryData ? (
            <>
              <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
                <div className="stat-card red"><div className="label">Available</div><div className="value">{inventoryData.inventory_stats?.total_available || 0}</div><div className="sub">vehicles in stock</div></div>
                <div className="stat-card"><div className="label">Avg Days on Lot</div><div className="value">{inventoryData.inventory_stats?.avg_days_on_lot || 0}</div><div className="sub">current inventory</div></div>
                <div className="stat-card"><div className="label">Over 30 Days</div><div className="value" style={{ color: inventoryData.inventory_stats?.over_30_days > 0 ? '#ff9800' : '#e0e0e0' }}>{inventoryData.inventory_stats?.over_30_days || 0}</div><div className="sub">need attention</div></div>
                <div className="stat-card"><div className="label">Over 60 Days</div><div className="value" style={{ color: inventoryData.inventory_stats?.over_60_days > 0 ? '#ff4444' : '#e0e0e0' }}>{inventoryData.inventory_stats?.over_60_days || 0}</div><div className="sub">price drop needed</div></div>
              </div>

              {inventoryData.by_make?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#cc0000', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Performance by Make</h3>
                  <table className="data-table">
                    <thead><tr><th>Make</th><th>Total Sold</th><th>Avg Days on Lot</th><th>Avg Sale Price</th><th>Avg Gross Profit</th></tr></thead>
                    <tbody>
                      {inventoryData.by_make.map((m, i) => (
                        <tr key={i}>
                          <td><strong>{m.make}</strong></td>
                          <td>{m.total_sold}</td>
                          <td style={{ color: m.avg_days_on_lot < 20 ? '#4caf50' : m.avg_days_on_lot > 45 ? '#ff4444' : '#ff9800' }}>{m.avg_days_on_lot} days</td>
                          <td>${Number(m.avg_sale_price || 0).toLocaleString()}</td>
                          <td style={{ color: '#4caf50' }}>${Number(m.avg_gross_profit || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {inventoryData.by_price_range?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#cc0000', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Performance by Price Range</h3>
                  <table className="data-table">
                    <thead><tr><th>Price Range</th><th>Total Sold</th><th>Avg Days on Lot</th></tr></thead>
                    <tbody>
                      {inventoryData.by_price_range.map((p, i) => (
                        <tr key={i}>
                          <td><strong>{p.price_range}</strong></td>
                          <td>{p.total_sold}</td>
                          <td style={{ color: p.avg_days_on_lot < 20 ? '#4caf50' : '#ff9800' }}>{p.avg_days_on_lot} days</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {inventoryData.slow_movers?.length > 0 && (
                <div>
                  <h3 style={{ color: '#ff4444', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>⚠️ Slow Movers — Over 30 Days</h3>
                  <table className="data-table">
                    <thead><tr><th>Vehicle</th><th>Price</th><th>Mileage</th><th>Days on Lot</th></tr></thead>
                    <tbody>
                      {inventoryData.slow_movers.map(v => (
                        <tr key={v.id}>
                          <td><strong>{v.year} {v.make} {v.model} {v.trim}</strong></td>
                          <td>${Number(v.price || 0).toLocaleString()}</td>
                          <td>{Number(v.mileage || 0).toLocaleString()} mi</td>
                          <td style={{ color: '#ff4444', fontWeight: 700 }}>{Math.round(v.days_on_lot)} days</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!inventoryData.by_make?.length && !inventoryData.slow_movers?.length && (
                <div className="empty-state"><div className="icon">📊</div><p>No sales data yet. Create deals to start generating inventory intelligence reports.</p></div>
              )}
            </>
          ) : null}
        </div>
      )}

      {tab === 'deals' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
            <select className="select-filter" value={period} onChange={e => { setPeriod(e.target.value); setDealData(null); }}>
              <option value="30">Last 30 Days</option>
              <option value="60">Last 60 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last 12 Months</option>
              <option value="9999">All Time</option>
            </select>
            <button className="btn-red" onClick={loadDeals}>Run Report</button>
          </div>

          {loading ? <div className="loading">Loading deal report...</div> : dealData ? (
            <>
              <div className="stats-grid">
                <div className="stat-card red"><div className="label">Total Deals</div><div className="value">{dealData.summary?.total_deals || 0}</div></div>
                <div className="stat-card"><div className="label">Total Revenue</div><div className="value" style={{ fontSize: 22 }}>${Number(dealData.summary?.total_revenue || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Total Gross Profit</div><div className="value" style={{ fontSize: 22, color: '#4caf50' }}>${Number(dealData.gross_profit?.total_gross_profit || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Avg Gross Profit</div><div className="value" style={{ fontSize: 22 }}>${Number(dealData.gross_profit?.avg_gross_profit || 0).toLocaleString()}</div></div>
              </div>

              <div className="stats-grid" style={{ marginBottom: 24 }}>
                <div className="stat-card"><div className="label">Payments Collected</div><div className="value" style={{ fontSize: 22, color: '#4caf50' }}>${Number(dealData.payment_stats?.total_collected || 0).toLocaleString()}</div></div>
                <div className="stat-card"><div className="label">Payments Paid</div><div className="value">{dealData.payment_stats?.paid || 0}</div></div>
                <div className="stat-card"><div className="label">Overdue Payments</div><div className="value" style={{ color: dealData.payment_stats?.overdue > 0 ? '#ff4444' : '#e0e0e0' }}>{dealData.payment_stats?.overdue || 0}</div></div>
                <div className="stat-card"><div className="label">Avg Down Payment</div><div className="value" style={{ fontSize: 22 }}>${Number(dealData.summary?.avg_down_payment || 0).toLocaleString()}</div></div>
              </div>

              {dealData.recent_deals?.length > 0 && (
                <div>
                  <h3 style={{ color: '#cc0000', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Recent Deals</h3>
                  <table className="data-table">
                    <thead><tr><th>Vehicle</th><th>Customer</th><th>Sale Price</th><th>Down Payment</th><th>Gross Profit</th><th>Date</th></tr></thead>
                    <tbody>
                      {dealData.recent_deals.map(d => (
                        <tr key={d.id}>
                          <td><strong>{d.year} {d.make} {d.model}</strong></td>
                          <td>{d.first_name} {d.last_name}</td>
                          <td>${Number(d.sale_price || 0).toLocaleString()}</td>
                          <td>${Number(d.down_payment_received || 0).toLocaleString()}</td>
                          <td style={{ color: '#4caf50' }}>${Number(d.gross_profit || 0).toLocaleString()}</td>
                          <td style={{ color: '#555', fontSize: 12 }}>{new Date(d.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!dealData.recent_deals?.length && (
                <div className="empty-state"><div className="icon">🤝</div><p>No deals in this period.</p></div>
              )}
            </>
          ) : null}
        </div>
      )}

      {tab === 'ai' && (
        <div>
          <p style={{ color: '#888', marginBottom: 20, fontSize: 14 }}>AI analyzes your sales history and generates personalized buying recommendations for your market.</p>
          {!recommendations ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <button className="btn-red" style={{ padding: '14px 32px', fontSize: 16 }} onClick={loadRecommendations} disabled={aiLoading}>
                {aiLoading ? <><span className="spinner" /> Analyzing your sales data...</> : '🤖 Generate AI Buying Recommendations'}
              </button>
              <p style={{ color: '#555', fontSize: 12, marginTop: 12 }}>Requires at least a few completed deals to generate meaningful insights</p>
            </div>
          ) : (
            <div>
              <div style={{ background: '#1a1a1a', border: '1px solid #cc0000', borderRadius: 10, padding: 24, marginBottom: 24 }}>
                <h3 style={{ color: '#cc0000', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>AI Analysis</h3>
                <p style={{ color: '#e0e0e0', lineHeight: 1.8, whiteSpace: 'pre-wrap', fontSize: 14 }}>{recommendations.recommendations}</p>
              </div>
              {recommendations.fast_sellers?.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ color: '#4caf50', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>✅ Your Fastest Sellers</h3>
                  <table className="data-table">
                    <thead><tr><th>Vehicle</th><th>Times Sold</th><th>Avg Days on Lot</th><th>Avg Sale Price</th></tr></thead>
                    <tbody>
                      {recommendations.fast_sellers.map((v, i) => (
                        <tr key={i}>
                          <td><strong>{v.year} {v.make} {v.model}</strong></td>
                          <td>{v.times_sold}</td>
                          <td style={{ color: '#4caf50' }}>{v.avg_days} days</td>
                          <td>${Number(v.avg_price || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button className="btn-outline" onClick={() => { setRecommendations(null); loadRecommendations(); }}>Regenerate Analysis</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
